import fullpage from '../lib/fullpage/fullpage.extensions.min'

import { hideInfoSection } from './infoSection'
import businessBook from './businessBook'
import { initProjectItemsNavigation, scrollActiveDotIntoView } from './projectItem'

const loadedImageIndexes = []
const loadedVideoIndexes = []
let volumeListener
let currentSlide = 0
let userMuted = localStorage.getItem('userMuted') !== 'false'
let scrollHintObserver = null

export const initFullPage = (page) => {
  const nativeInfoScrollPages = [
    'infographics',
    '_3d_animations',
    'virtual_tours',
    'virtual_reality'
  ]

  const useNativeInfoScroll = nativeInfoScrollPages.includes(page)

  if (useNativeInfoScroll && !document.getElementById('3ve-info-scroll-fix')) {
    const style = document.createElement('style')
    style.id = '3ve-info-scroll-fix'
    style.textContent = `
      .section.scroll {
        display: block !important;
        height: 100vh !important;
        max-height: 100vh !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        box-sizing: border-box !important;
        -webkit-overflow-scrolling: touch;
        overscroll-behavior-y: contain;
      }
    `
    document.head.appendChild(style)
  }

  console.log('init fullPage')

  listenToSectionButtonsClick()

  loadedImageIndexes.length = 0
  loadedVideoIndexes.length = 0

  // Fullpage doesn't allow initializing twice
  if (window.fullpage_api)
    window.fullpage_api.destroy('all')

  // noinspection JSPotentiallyInvalidConstructorUsage
  const fullPage = new fullpage('#fullpage', {
    scrollBar: false,
    scrollOverflow: !useNativeInfoScroll,
    scrolloverflowmacstyle: false,
    scrollOverflowReset: false,
    slideSelector: '.horizontal-scrolling',
    v2compatible: false,
    normalScrollElements: useNativeInfoScroll
      ? '#sidr, .selectable-container, .section.scroll'
      : '#sidr, .selectable-container',
    controlArrows: false,
    fadingEffect: 'slides',
    slidesNavigation: true,
    slidesNavPosition: 'bottom',
    navigationTooltips: ['firstSlide'],
    showActiveTooltip: true,
    loopHorizontal: true,
    menu: '#sidr',
    afterRender: function (_origin, destination, _direction) {
      const index = destination?.index
      const contentContainer =
        document.getElementById(index === undefined ? 'container-first-image' : `slide-index-${index}`)

      const isImage = !!contentContainer.dataset.image
      const isVideo = contentContainer.dataset.video === "true"
      const isIframe = contentContainer.dataset.iframe === "true"
      const isQuotePage = page === 'quote'

      const isProjectItem =
        page === 'infographics' || page === 'virtual_reality' || page === 'virtual_tours' || page === '_3d_animations'

      if (isProjectItem) {
        initProjectItemsNavigation()
        arrangeTagButtons()
      }

      if (isImage)
        showImage(contentContainer, contentContainer, index)

      else if (isVideo) {
        const video = document.getElementById(`video-${index}`)
        video.muted = userMuted
        showVolume(index, video)
        disableScroll()
      }

      else if (isIframe)
        disableScroll()

      if (isQuotePage) {
        const sideMenu = document.getElementById('side-menu')
        const logoContainer = document.getElementById('logo-container')

        sideMenu.style.color = '#222222'
        sideMenu.style.filter = 'none'
        logoContainer.classList.add('container-hidden')
      }

      showSlideInfo(index)
    },
    afterLoad: function (origin, destination, _direction) {    // sections ('up' and 'down')
      if (!fullPage)
        return

      const index = destination.index
      const contentContainer = document.getElementById(index === undefined ? 'container-first-image' : `slide-index-${index}`)

      const sideMenu = document.getElementById('side-menu')
      const logoContainer = document.getElementById('logo-container')
      const previousButton = document.getElementById('btn-previous')
      const nextButton = document.getElementById('btn-next')

      const isQuotePage = page === 'quote'
      const isContent = index === 0 && !isQuotePage
      const isInfoSection = index > 0 && !isQuotePage
      const isComingFromInfoSection = origin && origin.index > 0 && !isQuotePage
      const isHomeBookSection = index === 1 && page === 'index'
      const isVideo = contentContainer && contentContainer.dataset.video === "true"

      if (isContent) {
        enableScroll()

        if (useNativeInfoScroll)
          fullPage.setAllowScrolling(true, 'up,down')

        document.body.classList.remove('info-scroll-active')

        sideMenu.style.color = '#FFFFFF'
        sideMenu.style.filter = 'drop-shadow(1px 1px 0 rgba(0,0,0,0.9))'
        logoContainer.classList.remove('container-hidden')
        previousButton.classList.add('container-hidden')
        nextButton.classList.remove('container-hidden')

        if (isComingFromInfoSection && isVideo) {
          const video = document.getElementById(`video-${currentSlide}`)
          video.play()
        }
      }
      else if (isInfoSection) {
        enableScroll()

        if (useNativeInfoScroll)
          fullPage.setAllowScrolling(false, 'up,down')

        document.body.classList.add('info-scroll-active')

        sideMenu.style.color = '#222222'
        sideMenu.style.filter = 'none'
        logoContainer.classList.add('container-hidden')
        previousButton.classList.remove('container-hidden')
        nextButton.classList.add('container-hidden')

        if (isHomeBookSection)
          businessBook.isPresent() && businessBook.animateContent()
        else {
          showInfoSection(index)
          showScrollHint()
        }
      }
    },
    afterSlideLoad: function (_section, _origin, destination, _direction) {
      const index = destination?.index
      currentSlide = index

      scrollActiveDotIntoView(index)

      const contentContainer = document.getElementById(index === undefined ? 'container-first-image' : `slide-index-${index}`)
      const isImage = !!contentContainer.dataset.image
      const isVideo = contentContainer.dataset.video === "true"

      if (isImage) {
        const imageContainer = document.querySelector(`.image-${index}`)
        showImage(contentContainer, imageContainer, index)
      }
      else if (isVideo) {
        const video = document.getElementById(`video-${index}`)
        const alreadyLoaded = loadedVideoIndexes.includes(index)

        video.muted = userMuted

        if (!alreadyLoaded) {
          video.src = video.dataset.source
          loadedVideoIndexes.push(index)
          video.load()
          video.addEventListener('canplay', () => video.play().catch(() => {}), { once: true })
        } else {
          video.play().catch(() => {})
        }

        showVolume(index, video)
      }

      showSlideInfo(index)
    },
    onSlideLeave (section, origin) {
      removeVolumeListener()
      const video = document.getElementById(`video-${origin?.index}`)
      if (video) video.pause()
    },
    onLeave: function (origin, destination, _direction) {
      hideScrollHint()

      const isSection = document.getElementById(`info-section-${origin.index - 1}`)
      const isInfoSection = isSection && origin.index > 0

      if (isInfoSection)
        hideInfoSection(origin.index)
    },
  })

  window.fullpage_api = fullPage
  

  function listenToSectionButtonsClick() {
    document.getElementById('btn-previous').onclick = () => fullPage.moveSectionUp()
    document.getElementById('btn-next').onclick = () => fullPage.moveSectionDown()
  }
}

function arrangeTagButtons() {
  const tagButtonContainers = document.querySelectorAll('.tag-btns-container-row-2')

  tagButtonContainers.forEach(container => {
    if (container.children.length === 4)
      container.classList.add('md-justify-center')
  })
}

function showImage(contentContainer, imageContainer, slideIndex) {
  const alreadyLoaded = loadedImageIndexes.includes(slideIndex)

  if (alreadyLoaded) {
    setTimeout(() =>
      document.getElementById(`info-container-${slideIndex}`).classList.remove("container-hidden"), 1500)
  }
  else {
    const img = new Image()
    const imageUrl = imageContainer.dataset.sizedImage

    if (imageUrl)
      img.src = imageUrl
    else
      observeChangesToImageUrl(imageContainer, imageUrl => img.src = imageUrl)

    function onImageLoad() {
      imageContainer.style.backgroundImage = `url(${img.src})`
      imageContainer.style.backgroundSize = 'cover'
      imageContainer.style.opacity = 0

      requestAnimationFrame(() => {
        imageContainer.style.transition = 'opacity 0.6s ease'
        imageContainer.style.opacity = 1
      })

      loadedImageIndexes.push(slideIndex)
    }

    img.onload = onImageLoad
  }
}

const observeChangesToImageUrl = (imageContainer, action) => {
  const observer = new MutationObserver((mutationsList) => {
    for (const mutation of mutationsList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-sized-image') {
        const imageUrl = imageContainer.dataset.sizedImage
        action(imageUrl)
      }
    }
  })

  observer.observe(imageContainer, {
    attributes: true,                 // listen for attribute changes
    attributeFilter: ['data-sized-image'],  // only watch this specific attribute
  })
}

function showVolume(index, video) {
  const volume = document.getElementById('volume')
  volume.style.display = 'grid'

  showVolumeIcon(volume, userMuted)

  volumeListener = () => {
    userMuted = !userMuted
    localStorage.setItem('userMuted', userMuted)

    video.muted = userMuted
    if (!userMuted) video.play().catch(() => {})

    showVolumeIcon(volume, userMuted)
  }

  volume.addEventListener('click', volumeListener)
}

function removeVolumeListener() {
  const volume = document.getElementById('volume')
  volume.removeEventListener('click', volumeListener)
}

function showVolumeIcon(volume, muted) {
  const volumeIcon = volume.querySelector('i')

  if (muted) {
    volumeIcon.classList.add('fa-volume-xmark')
    volumeIcon.classList.remove('fa-volume-low')
  }
  else {
    volumeIcon.classList.add('fa-volume-low')
    volumeIcon.classList.remove('fa-volume-xmark')
  }
}

function showSlideInfo (index) {
  if (index)
    setTimeout(() => {
      const infoContainer = document.getElementById(`info-container-${index}`)
      infoContainer.classList.remove("container-hidden")
    }, 2000)
}

function disableScroll() {
  document.body.style.overflow = 'hidden'
}

function enableScroll() {
  document.body.style.overflow = ''
}

function showInfoSection() {
  const isMobile = window.innerWidth < 768

  const observer = new IntersectionObserver((entries, observer) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible')
        observer.unobserve(entry.target) // only animate once
      }
    })
  },
  { rootMargin: `0px 0px ${isMobile ? '-200px' : '-400px'} 0px`})  // bottom margin shrunk by 400px - show elements when they are 400px in the screen


  document.querySelectorAll('.reveal').forEach(reveal => observer.observe(reveal))
}

function showScrollHint() {
  const lastSection = document.querySelector('.info-section.last')
  if (!lastSection) return

  let hint = document.getElementById('scroll-hint')
  if (!hint) {
    hint = document.createElement('div')
    hint.id = 'scroll-hint'
    hint.innerHTML = '<i class="fa-solid fa-chevron-down"></i>'
    document.body.appendChild(hint)
  }

  scrollHintObserver?.disconnect()

  hint.classList.remove('scroll-hint-hidden')

  scrollHintObserver = new IntersectionObserver((entries) => {
    hint.classList.toggle('scroll-hint-hidden', entries[0].isIntersecting)
  }, { threshold: 0.5 })

  scrollHintObserver.observe(lastSection)
}

function hideScrollHint() {
  document.getElementById('scroll-hint')?.classList.add('scroll-hint-hidden')
  scrollHintObserver?.disconnect()
  scrollHintObserver = null
}
